import { isEmpty, isPresent } from '@ember/utils'
import { action } from '@ember/object'
import type { DataOperationsConfig } from 'teacher-ui-client/components/data-operations/data-operations-proxy'

export interface Identifiable {
  id: string | number
}

export class RecordSelector<RecordType extends Identifiable> {
  private dataOperationsConfig: DataOperationsConfig<RecordType>

  constructor(dataOperationsConfig: DataOperationsConfig<RecordType>) {
    this.dataOperationsConfig = dataOperationsConfig
  }

  get noneSelected() {
    return !this.anySelected
  }

  get oneSelected() {
    return this.dataOperationsConfig.selectedIds?.length === 1
  }

  get anySelected() {
    if (!this.dataOperationsConfig.selectedIds?.length) return
    return this.dataOperationsConfig.selectedIds.length >= 1
  }

  get noSelectedRecords() {
    return isEmpty(this.selectedRecords)
  }

  get allSelected() {
    return Boolean(
      this.dataOperationsConfig.selectedIds?.length &&
      this.dataOperationsConfig.data.slice().every((item: RecordType) => {
        return this.dataOperationsConfig.selectedIds?.includes(String(item.id))
      }),
    )
  }

  get selectedIds() {
    return this.dataOperationsConfig.selectedIds
  }

  get selectedRecords(): RecordType[] {
    const { noneSelected } = this
    const { allSelected } = this
    const { data } = this.dataOperationsConfig

    if (isEmpty(data)) return []
    if (noneSelected) return []
    if (allSelected) return data.slice()

    const selectedIds = this.dataOperationsConfig.selectedIds ?? []
    const selectedIdsAsStrings = selectedIds.map(String)
    return data.slice().filter((item) => {
      return selectedIdsAsStrings.includes(String(item.id))
    })
  }

  @action
  toggleAll() {
    if (this.allSelected) {
      this.dataOperationsConfig.selectedIds = []
    } else {
      this.dataOperationsConfig.selectedIds = this.dataOperationsConfig.data.slice().map((item) => String(item.id))
    }
  }

  @action
  toggle(id: Identifiable['id']) {
    if (!isPresent(id) || !this.dataOperationsConfig.selectedIds) return

    if (this.dataOperationsConfig.selectedIds?.includes(String(id))) {
      this.dataOperationsConfig.selectedIds = this.dataOperationsConfig.selectedIds.filter(
        (selectedId: string) => selectedId !== String(id),
      )
    } else {
      this.dataOperationsConfig.selectedIds = [...this.dataOperationsConfig.selectedIds, String(id)]
    }
  }
}
